import React, { useMemo, useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  Platform,
} from 'react-native';
import MaterialIcons from '@expo/vector-icons/MaterialIcons';
import { useTheme } from '../context/ThemeContext';
import { BASE_COURSE_TYPES } from '../utils/courseTypes';
import { getCourseTypeDotStyle } from '../utils/courseTypeColors';
import { createEmptyCalendarFilters, hasActiveCalendarFilters } from '../utils/trainingCalendarFilters';

const LIGHT_CREAM = '#faf8f4';

function toggleSetValue(set, value, key = value) {
  const next = new Set(set);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  return next;
}

export default function TrainingCalendarExerciseFilters({
  assignedCourses = [],
  filters,
  onFiltersChange,
  onRefresh,
  compact = false,
}) {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const gold = theme.colors.primary || '#d4af37';
  const styles = useMemo(() => getStyles(theme, gold, compact), [theme, gold, compact]);

  const activeFilters = filters || createEmptyCalendarFilters();
  const filterActive = hasActiveCalendarFilters(activeFilters);

  const triggerLabel = useMemo(() => {
    if (!filterActive) return 'Exercise Filters';
    const parts = [];
    if (activeFilters.selectedTypes.size > 0) {
      parts.push(`${activeFilters.selectedTypes.size} type(s)`);
    }
    if (activeFilters.selectedCourses.size > 0) {
      parts.push(`${activeFilters.selectedCourses.size} course(s)`);
    }
    return parts.length ? `Exercise Filters (${parts.join(', ')})` : 'Exercise Filters';
  }, [activeFilters, filterActive]);

  const handleToggleType = (typeLabel) => {
    const key = typeLabel.toLowerCase();
    onFiltersChange({
      ...activeFilters,
      selectedTypes: toggleSetValue(activeFilters.selectedTypes, typeLabel, key),
    });
  };

  const handleToggleCourse = (courseName) => {
    onFiltersChange({
      ...activeFilters,
      selectedCourses: toggleSetValue(activeFilters.selectedCourses, courseName),
    });
  };

  const handleRefresh = () => {
    onRefresh?.();
    setOpen(false);
  };

  return (
    <View
      style={styles.row}
      {...(Platform.OS === 'web' ? { dataSet: { trainingCalendarFilterRow: 'true' } } : {})}
    >
      <TouchableOpacity
        style={styles.trigger}
        onPress={() => setOpen(true)}
        activeOpacity={0.85}
        accessibilityRole="button"
        accessibilityLabel="Exercise Filters"
        {...(Platform.OS === 'web' ? { dataSet: { trainingCalendarFilterTrigger: 'true' } } : {})}
      >
        <Text style={styles.triggerText} numberOfLines={1}>
          {triggerLabel}
        </Text>
        <Text style={styles.triggerChevron}>▼</Text>
      </TouchableOpacity>

      <TouchableOpacity
        style={styles.refreshBtn}
        onPress={handleRefresh}
        activeOpacity={0.85}
        accessibilityRole="button"
        accessibilityLabel="Reset calendar filters"
        {...(Platform.OS === 'web' ? { dataSet: { trainingCalendarFilterRefresh: 'true' } } : {})}
      >
        <MaterialIcons name="refresh" size={22} color={theme.colors.heading} />
      </TouchableOpacity>

      <Modal visible={open} transparent animationType="fade" onRequestClose={() => setOpen(false)}>
        <View style={styles.modalRoot}>
          <Pressable style={styles.modalBackdrop} onPress={() => setOpen(false)} />
          <View
            style={styles.modalSheet}
            {...(Platform.OS === 'web' ? { dataSet: { trainingCalendarFilterSurface: 'true' } } : {})}
          >
            <Text style={styles.modalTitle}>Exercise Filters</Text>
            <ScrollView style={styles.modalScroll} keyboardShouldPersistTaps="handled">
              {BASE_COURSE_TYPES.map((typeLabel) => {
                const checked = activeFilters.selectedTypes.has(typeLabel.toLowerCase());
                const dotStyle = getCourseTypeDotStyle(typeLabel, {
                  isDarkMode: theme.isDarkMode,
                });
                return (
                  <TouchableOpacity
                    key={typeLabel}
                    style={styles.filterRow}
                    onPress={() => handleToggleType(typeLabel)}
                    activeOpacity={0.75}
                    accessibilityRole="checkbox"
                    accessibilityState={{ checked }}
                    {...(Platform.OS === 'web'
                      ? { dataSet: { trainingCalendarFilterTypeRow: 'true' } }
                      : {})}
                  >
                    <Text style={styles.checkboxMark}>{checked ? '☑' : '☐'}</Text>
                    <Text style={[styles.typeDot, { color: dotStyle.color }]}>●</Text>
                    <Text style={styles.filterRowLabel}>{typeLabel}</Text>
                  </TouchableOpacity>
                );
              })}

              <View style={styles.divider} />

              {assignedCourses.length === 0 ? (
                <Text style={styles.emptyCoursesHint}>No assigned courses on your profile.</Text>
              ) : (
                assignedCourses.map((courseName) => {
                  const checked = activeFilters.selectedCourses.has(courseName);
                  return (
                    <TouchableOpacity
                      key={courseName}
                      style={styles.filterRow}
                      onPress={() => handleToggleCourse(courseName)}
                      activeOpacity={0.75}
                      accessibilityRole="checkbox"
                      accessibilityState={{ checked }}
                      {...(Platform.OS === 'web'
                        ? { dataSet: { trainingCalendarFilterCourseRow: 'true' } }
                        : {})}
                    >
                      <Text style={styles.checkboxMark}>{checked ? '☑' : '☐'}</Text>
                      <Text style={styles.filterRowLabel}>{courseName}</Text>
                    </TouchableOpacity>
                  );
                })
              )}
            </ScrollView>
            <TouchableOpacity style={styles.doneBtn} onPress={() => setOpen(false)} activeOpacity={0.85}>
              <Text style={styles.doneBtnText}>Done</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    </View>
  );
}

function getStyles(theme, gold, compact) {
  return StyleSheet.create({
    row: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 10,
      marginHorizontal: compact ? 0 : 8,
      marginTop: compact ? 10 : 12,
      marginBottom: compact ? 4 : 0,
      backgroundColor: 'transparent',
    },
    trigger: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'center',
      paddingVertical: 12,
      paddingHorizontal: 14,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: theme.colors.border,
      backgroundColor: theme.isDarkMode
        ? theme.colors.inputBackground || theme.colors.surface
        : LIGHT_CREAM,
    },
    triggerText: {
      flex: 1,
      fontSize: 15,
      fontWeight: '600',
      color: theme.colors.text,
      backgroundColor: 'transparent',
    },
    triggerChevron: {
      fontSize: 11,
      color: theme.colors.text,
      opacity: 0.7,
      marginLeft: 8,
      backgroundColor: 'transparent',
    },
    refreshBtn: {
      width: 44,
      height: 44,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: theme.colors.border,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme.isDarkMode
        ? theme.colors.inputBackground || theme.colors.surface
        : LIGHT_CREAM,
    },
    modalRoot: {
      flex: 1,
      justifyContent: 'center',
      padding: 24,
    },
    modalBackdrop: {
      ...StyleSheet.absoluteFillObject,
      backgroundColor: 'rgba(0,0,0,0.45)',
    },
    modalSheet: {
      maxHeight: '75%',
      backgroundColor: theme.isDarkMode ? theme.colors.surface : LIGHT_CREAM,
      borderRadius: 12,
      padding: 16,
      borderWidth: 1,
      borderColor: theme.colors.border,
    },
    modalTitle: {
      fontSize: 17,
      fontWeight: '700',
      color: theme.colors.text,
      marginBottom: 12,
      backgroundColor: 'transparent',
    },
    modalScroll: {
      backgroundColor: 'transparent',
    },
    filterRow: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingVertical: 12,
      paddingHorizontal: 8,
      borderRadius: 8,
      marginBottom: 4,
      backgroundColor: 'transparent',
    },
    checkboxMark: {
      fontSize: 18,
      marginRight: 10,
      color: theme.colors.text,
      backgroundColor: 'transparent',
    },
    typeDot: {
      fontSize: 14,
      marginRight: 8,
      backgroundColor: 'transparent',
    },
    filterRowLabel: {
      flex: 1,
      fontSize: 16,
      color: theme.colors.text,
      backgroundColor: 'transparent',
    },
    divider: {
      height: 1,
      backgroundColor: theme.colors.border,
      marginVertical: 12,
    },
    emptyCoursesHint: {
      fontSize: 14,
      color: theme.colors.text,
      opacity: 0.7,
      fontStyle: 'italic',
      paddingHorizontal: 8,
      paddingBottom: 8,
      backgroundColor: 'transparent',
    },
    doneBtn: {
      marginTop: 12,
      alignSelf: 'center',
      paddingVertical: 10,
      paddingHorizontal: 24,
      borderRadius: 8,
      backgroundColor: gold,
    },
    doneBtnText: {
      fontSize: 15,
      fontWeight: '700',
      color: '#000000',
      backgroundColor: 'transparent',
    },
  });
}
